import { RouterProvider, createRouter } from '@tanstack/react-router';
import { QueryClientProvider } from '@tanstack/react-query';
import ReactDOM from 'react-dom/client';
import { ErrorBoundary } from 'react-error-boundary';

import '@repo/ui/CSSBase/root.css';
import '@repo/ui/CSSBase/font-figtree.css';
import { ErrorBoundaryFallback } from '@repo/ui/ErrorBoundaryFallback';
import { Flex } from '@repo/ui/Flex';
import { ToastRoot } from '@repo/ui/Toast/ToastRoot';
import { rawString } from './components/Language/Language';
import { LanguageProviderWrapper } from './components/Language/LanguageProviderWrapper';
import { queryClient } from './queryClient';
import { routeTree } from './routeTree.gen';
import { clientLogger } from './utils/logger';

if (typeof window !== 'undefined') {
  window.addEventListener('error', (e) => {
    clientLogger.logError(e.error);
  });
  window.addEventListener('unhandledrejection', (e) => {
    clientLogger.logError(e.reason);
  });
}

const errorBoundaryTitle =
  rawString({ langKey: 'pdx.error.boundary.title' }) ?? '';

const router = createRouter({
  routeTree,
  defaultErrorComponent: ({ error, info: _info }) => {
    const { correlationId } = clientLogger.logError(
      error,
      'Router error boundary',
    );
    const correlationIdStr = rawString({
      langKey: 'pdx.global.correlation_id',
      replacementTokens: { correlation_id: correlationId },
    });
    return (
      <Flex alignItems="center" justifyContent="center" padding="p-8">
        <ErrorBoundaryFallback
          metaInfo={correlationIdStr}
          message={error.message}
          title={errorBoundaryTitle}
        />
      </Flex>
    );
  },
  defaultPreload: 'intent',
  scrollRestoration: true,
});

// Register things for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}

const rootElement = document.getElementById('app')!;

if (!rootElement.innerHTML) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <ErrorBoundary
      fallbackRender={({ error }) => {
        const { correlationId } = clientLogger.logError(error);
        const message = error instanceof Error ? error.message : String(error);
        return (
          <ErrorBoundaryFallback
            message={message}
            title={errorBoundaryTitle}
            metaInfo={rawString({
              langKey: 'pdx.global.correlation_id',
              replacementTokens: { correlation_id: correlationId },
            })}
          />
        );
      }}
    >
      <QueryClientProvider client={queryClient}>
        <LanguageProviderWrapper>
          <ToastRoot />
          <RouterProvider router={router} />
        </LanguageProviderWrapper>
      </QueryClientProvider>
    </ErrorBoundary>,
  );
}
